import PhotoSwipe           from 'photoswipe';
import { decode as bhDecode } from 'blurhash';
import { Application, Container, Sprite, Texture } from 'pixi.js';
import { renderFilterButtons } from './filter-ui.js';
import { resetCrumbTracking } from './gallery-breadcrumb.js';
import { bindPointerEvents, applyInertia, stepZoom } from './pointer-input.js';
import { showShare, hideShare } from './share.js';
import {
    IS_IOS, MOBILE_W, MOUNT_BATCH_IOS_MOVING, TIERS, tierForZoom,
    evictFarTextures, ensureTier, drainQueue, flushDeferredDestroys,
    installContextGuard, removeContextGuard, deferDestroy, stopTierLoads,
} from './gallery-virt.js';

// ── Layout ────────────────────────────────────────────────────────────────────
const WALL_W    = 260;
const WALL_H    = 260;
const COLS      = 10;
const COL_GAP   = 80;
const ROW_GAP   = 50;
const ROW_JITTER = 60;

// ── Behaviour ─────────────────────────────────────────────────────────────────
const ZOOM_MIN        = 0.05;
const ZOOM_MIN_MOBILE = 0.25; // clamp mobile zoom-out: avoids the draw-call cost of hundreds of unique tier-0 textures
const ZOOM_MAX        = 4;
const ZOOM_LIGHTBOX = 1.8;
const MOUNT_BATCH  = 20;
const CLAMP_MIN_PX_DESKTOP = 400; // min screen-px of content always visible at edges
const CLAMP_MIN_PX_MOBILE  = 60;

// Wall categories — labels for the filter UI and breadcrumb.
const CAT_LABELS = { press: 'Press', invite: 'Invites', campaign: 'Campaigns' };

function jitter(seed, range) {
    const x = Math.sin(seed * 9301 + 49297) * 233280;
    return ((x - Math.floor(x)) - 0.5) * range;
}

// ── WallGallery ───────────────────────────────────────────────────────────────
class WallGallery {
    constructor() {
        this.viewport = document.getElementById('wall-viewport');
        if (!this.viewport) return;

        this.data    = [];
        this.items   = [];
        this.worldW  = 0;
        this.worldH  = 0;

        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.velX = 0;
        this.velY = 0;
        this.W    = window.innerWidth;
        this.H    = window.innerHeight;

        this.zoomVel  = 0;
        this.zoomCX   = 0;
        this.zoomCY   = 0;
        this.ZOOM_LEVELS = [];
        this.ZOOM_MIN = window.innerWidth < MOBILE_W ? ZOOM_MIN_MOBILE : ZOOM_MIN;
        this.ZOOM_MAX = ZOOM_MAX;

        this._raf          = null;
        this._dragging     = false;
        this._activeFilter = 'all';
        // Mobile (iOS or < tablet width): show ONE category at a time and redraw
        // on switch — the full ~200-image WebGL wall crashes iOS. Desktop keeps
        // the whole wall with the usual All + per-type visibility filter.
        this._mobile         = IS_IOS || window.innerWidth < MOBILE_W;
        this._activeCategory = null;
        this._categoryList   = [];
        this._filterCrumb  = '';
        this._crumbItem    = null;
        this._crumbInitialised = false;
        this._crumbTrackingStarted = false;
        this._crumbVisible = false;
        this._lastPanKey   = '';
        this.lbIndex         = new Map();
        this._mounted        = new Set();
        this._gen            = 0;
        this._tierLoading    = 0;
        this._tierPending    = new Map(); // itemId → { item, tier, dist } — dedup by item

        this._pixiApp = null;
        this._world   = null;
        this._itemW   = WALL_W; // gallery-virt eviction reads this

        this.init();
    }

    // ── Init ──────────────────────────────────────────────────────────────────

    destroy() {
        stopTierLoads(this); // abort in-flight thumb fetches so swup nav doesn't keep loading
        if (this._raf !== null) { cancelAnimationFrame(this._raf); this._raf = null; }
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        removeContextGuard(this);
        // PixiApp.destroy() does not reach textures stashed on item objects —
        // release them explicitly so Swup nav doesn't leak VRAM.
        for (const item of this.items) {
            item._texture?.destroy(true);
            item._bhTex?.destroy(true);
            item._texture = null;
            item._bhTex   = null;
        }
        this._pixiApp?.destroy(true);
    }

    async init() {
        this._onResize = () => this.onResize();
        window.addEventListener('resize', this._onResize);
        await this.loadData();

        this._pixiApp = new Application();
        await this._pixiApp.init({
            backgroundAlpha: 0,
            resizeTo:        this.viewport,
            antialias:       false,
            resolution:      window.innerWidth < MOBILE_W
                ? 1
                : Math.min(window.devicePixelRatio || 1, 2),
            autoDensity:     true,
            preference:      'webgl',
        });
        this._pixiApp.ticker.stop();
        this.viewport.appendChild(this._pixiApp.canvas);
        this._world = new Container();
        this._pixiApp.stage.addChild(this._world);

        installContextGuard(this);

        this.buildLayout();
        this.bindEvents();
        this._wake();
        this.checkUrlParam();
    }

    // ── Resize ────────────────────────────────────────────────────────────────

    onResize() {
        this.W = window.innerWidth;
        this.H = window.innerHeight;
        this.ZOOM_MIN = this.W < MOBILE_W ? ZOOM_MIN_MOBILE : ZOOM_MIN;
        if (this.worldH > 0) this._syncWorld();
    }

    // ── Data ──────────────────────────────────────────────────────────────────

    async loadData() {
        const isAccessories = this.viewport.dataset.mode === 'accessories';
        const filter = isAccessories
            ? "type='accessory'"
            : "type='press' || type='invite' || type='campaign'";
        try {
            const res = await fetch(
                `${window.JK.pbUrl}/api/collections/items/records?` +
                new URLSearchParams({
                    perPage: 500,
                    filter,
                    fields:  'id,collectionId,title,type,file,img_blurhash,img_w,img_h',
                    sort:    'type,title',
                })
            );
            const json = await res.json();
            this.data = json.items || [];
        } catch (err) {
            console.error('WallGallery: load failed', err);
        }
    }

    // ── Layout ────────────────────────────────────────────────────────────────

    buildLayout() {
        for (const item of this.items) this._unmountItem(item);

        // Category list for the switcher (all categories present in the data).
        const seenCat = new Set();
        this._categoryList = [];
        for (const it of this.data) {
            if (it.type && !seenCat.has(it.type)) { seenCat.add(it.type); this._categoryList.push(it.type); }
        }

        // Mobile: scope the world to one category and free the GPU textures held
        // by the others, so only one category's images ever live in WebGL.
        if (this._mobile) {
            if (!this._activeCategory || !seenCat.has(this._activeCategory)) {
                this._activeCategory = this._resolveInitialCategory();
            }
            this._filterCrumb = CAT_LABELS[this._activeCategory] || this._activeCategory;
            for (const it of this.data) {
                if (it.type !== this._activeCategory) this._releaseItem(it);
            }
        }

        const source = this._mobile
            ? this.data.filter(i => i.type === this._activeCategory)
            : this.data;
        const pool = [...source];
        pool.sort((a, b) => {
            const sa = Math.sin(a.id.charCodeAt(0) * 9301) * 233280;
            const sb = Math.sin(b.id.charCodeAt(0) * 9301) * 233280;
            return (sa - Math.floor(sa)) - (sb - Math.floor(sb));
        });

        const cols = Math.min(COLS, Math.max(1, pool.length));
        const colH = new Array(cols).fill(0);
        for (let c = 0; c < cols; c++) {
            colH[c] = jitter(c, ROW_JITTER * 2) + ROW_JITTER;
        }

        this.items = [];

        pool.forEach((item, idx) => {
            const col  = idx % cols;
            item._h   = (item.img_w && item.img_h)
                ? Math.round(WALL_W * item.img_h / item.img_w)
                : WALL_H;
            item._x          = col * (WALL_W + COL_GAP) + jitter(idx, 20);
            item._y          = colH[col];
            item._el         = null;
            item._loadedTier = -1;
            this.items.push(item);
            colH[col] += item._h + ROW_GAP + Math.abs(jitter(idx + 100, ROW_JITTER));
        });

        this.worldW = cols * (WALL_W + COL_GAP) - COL_GAP;
        this.worldH = Math.max(...colH, 1);

        this.lbIndex = new Map(this.items.map((item, i) => [item.id, i]));
        this._sortedItems = [...this.items].sort((a, b) => a._y - b._y);

        this._renderFilterButtons();
        this._zoomToFit();
    }

    // Mobile only: which category to show first — explicit ?filter=, else the
    // category of a deep-linked ?item=, else the first category in the data.
    _resolveInitialCategory() {
        const params = new URLSearchParams(location.search);
        const want   = params.get('filter');
        if (want && this._categoryList.includes(want)) return want;
        const itemParam = params.get('item');
        if (itemParam) {
            const it = this.data.find(i => i.id === itemParam || this.titleToSlug(i.title) === itemParam);
            if (it?.type) return it.type;
        }
        return this._categoryList[0] || 'press';
    }

    // Free an off-category item's GPU resources when the mobile scope changes.
    _releaseItem(item) {
        if (item._sprite) {
            item._sprite.destroy({ texture: false });
            item._sprite = null;
            item._el     = null;
            this._mounted.delete(item);
        }
        if (item._texture) { deferDestroy(this, item._texture); item._texture = null; }
        if (item._bhTex)   { deferDestroy(this, item._bhTex);   item._bhTex   = null; }
        item._cachedSrc  = null;
        item._cachedTier = -1;
        item._loadedTier = -1;
    }

    _zoomToFit() {
        // Fit to viewport width so columns space evenly across the screen.
        // World scrolls vertically. Only called on initial load — resize
        // preserves the user's current zoom/pan.
        this.zoom = Math.max(this.ZOOM_MIN, Math.min(this.W / this.worldW * 0.95, ZOOM_MAX));
        this.panX = (this.W - this.worldW * this.zoom) / 2;
        this.panY = 80;

        const fitZ = this.zoom;
        this.ZOOM_LEVELS = [
            fitZ,
            Math.min(fitZ * 4,  ZOOM_MAX),
            Math.min(fitZ * 10, ZOOM_MAX),
            Math.min(1.0,       ZOOM_MAX),
        ].filter((z, i, a) => i === 0 || z > a[i - 1] * 1.2);

        this._syncWorld();
    }

    // ── Filter ────────────────────────────────────────────────────────────────

    setFilter(filter) {
        // Mobile: each "filter" is a category swap — rebuild the world scoped to
        // it (redraw) so only that category's images live in WebGL at once.
        if (this._mobile) {
            if (!filter || filter === 'all') filter = this._categoryList[0];
            if (!filter || filter === this._activeCategory) return;
            this._activeCategory = filter;
            this.buildLayout();           // re-scopes, frees other categories, refits
            this._wake();
            this._filterCrumb = CAT_LABELS[filter] || filter;
            resetCrumbTracking(this);
            history.replaceState(null, '', `${location.pathname}?filter=${filter}`);
            return;
        }
        this._activeFilter = filter || 'all';
        this._filterCrumb = this._activeFilter !== 'all'
            ? (CAT_LABELS[this._activeFilter] || this._activeFilter) : '';
        resetCrumbTracking(this);
        for (const item of this.items) {
            if (item._sprite) {
                item._sprite.visible = this._itemVisible(item);
            }
        }
        if (this._pixiApp) this._pixiApp.renderer.render(this._pixiApp.stage);
        this._fitVisible();

        const params = new URLSearchParams(location.search);
        if (this._activeFilter === 'all') params.delete('filter');
        else params.set('filter', this._activeFilter);
        const qs = params.toString();
        history.replaceState(null, '', location.pathname + (qs ? `?${qs}` : ''));
    }

    _fitVisible() {
        const visible = this.items.filter(i => this._itemVisible(i));
        if (!visible.length) return;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const item of visible) {
            minX = Math.min(minX, item._x);
            minY = Math.min(minY, item._y);
            maxX = Math.max(maxX, item._x + WALL_W);
            maxY = Math.max(maxY, item._y + item._h);
        }
        const pad = 60;
        const targetZ = Math.min(this.W / (maxX - minX + pad * 2), this.H / (maxY - minY + pad * 2), ZOOM_MAX);
        const endX    = this.W / 2 - ((minX + maxX) / 2) * targetZ;
        const endY    = this.H / 2 - ((minY + maxY) / 2) * targetZ;

        const startZ = this.zoom, startX = this.panX, startY = this.panY;
        this.zoomVel = 0; this.velX = this.velY = 0;
        const startTime = performance.now();
        const tick = now => {
            const t = Math.min(1, (now - startTime) / 450);
            const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            this.zoom = startZ + (targetZ - startZ) * e;
            this.panX = startX + (endX - startX) * e;
            this.panY = startY + (endY - startY) * e;
            this._syncWorld();
            if (t < 1) requestAnimationFrame(tick);
            else this._wake();
        };
        requestAnimationFrame(tick);
    }

    _itemVisible(item) {
        return this._activeFilter === 'all' || item.type === this._activeFilter;
    }

    _renderFilterButtons() {
        // Build from the full category list so the mobile switcher shows every
        // category even though only one is mounted at a time.
        const groups = this._categoryList.map(t => ({ key: t, label: CAT_LABELS[t] || t }));
        if (!groups.length) return;
        const nav    = document.getElementById('wall-filter');
        const active = this._mobile ? this._activeCategory : this._activeFilter;
        renderFilterButtons(nav, groups, active, key => {
            this.setFilter(key);
        }, { includeAll: !this._mobile });
    }

    // ── Sprite virtualiser ────────────────────────────────────────────────────

    updateScene() {
        const tier = tierForZoom(this.zoom, WALL_W);
        const vl = -this.panX / this.zoom;
        const vt = -this.panY / this.zoom;
        const vr = vl + this.W / this.zoom;
        const vb = vt + this.H / this.zoom;
        const cx = vl + (vr - vl) / 2;
        const cy = vt + (vb - vt) / 2;
        // Mobile: tight buffer so most cached items are unmounted and therefore
        // safe to evict. Desktop keeps the generous one-viewport buffer.
        const buf = (this.W < MOBILE_W) ? this.H / this.zoom * 0.25 : this.H / this.zoom;

        const moving   = this._dragging || Math.abs(this.velX) > 0.3 || Math.abs(this.velY) > 0.3 || Math.abs(this.zoomVel) > 0.001;
        const maxMount = moving
            ? (IS_IOS ? MOUNT_BATCH_IOS_MOVING : 5)
            : (this.W < MOBILE_W ? 8 : MOUNT_BATCH);
        const now = performance.now();
        if (moving) this._lastMoveAt = now;
        const settled = !moving && (now - (this._lastMoveAt || 0)) > 150;

        const gen = ++this._gen;
        let mounted = 0, pending = false;

        const si = this._sortedItems;
        let lo = 0, hi = si.length - 1, first = si.length;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (si[mid]._y + si[mid]._h > vt - buf) { first = mid; hi = mid - 1; }
            else lo = mid + 1;
        }

        const isMobile = this.W < MOBILE_W;
        for (let i = first; i < si.length; i++) {
            const item = si[i];
            if (item._y > vb + buf) break;
            if (item._x >= vr + buf || item._x + WALL_W <= vl - buf) {
                if (item._el) this._unmountItem(item);
                continue;
            }
            item._visitGen = gen;
            if (!item._el) {
                if (mounted >= maxMount) { pending = true; continue; }
                this._mountItem(item);
                item._sprite.visible = this._itemVisible(item);
                mounted++;
            }
            if (settled && this._itemVisible(item)) {
                const dist = Math.abs(item._x + WALL_W / 2 - cx) + Math.abs(item._y + item._h / 2 - cy);
                // Mobile: only items in the unbuffered viewport may load tier 2.
                const onscreen = item._x < vr && item._x + WALL_W > vl
                              && item._y < vb && item._y + item._h > vt;
                const cappedTier = (isMobile && !onscreen) ? Math.min(tier, 1) : tier;
                ensureTier(this, item, cappedTier, dist);
            }
        }

        for (const item of this._mounted) {
            if (item._visitGen !== gen) this._unmountItem(item);
        }

        if (settled) {
            drainQueue(this);
            evictFarTextures(this);
        } else if (isMobile) {
            // Run eviction every frame on mobile during motion — see
            // gallery-animate-zoom for rationale. Cheap when nothing to do.
            evictFarTextures(this);
        }
        return pending || (!moving && !settled);
    }

    // gallery-virt's evictFarTextures uses this to compute world-Y for distance.
    _itemY(item) { return item._y; }

    _mountItem(item) {
        let tex = Texture.EMPTY;
        let tint = 0xFFFFFF;
        if (item._texture) tex = item._texture;
        if (tex === Texture.EMPTY) {
            if (this.W < MOBILE_W) {
                // Mobile: skip the per-item canvas blurhash texture entirely.
                // Use Texture.WHITE (singleton) tinted with the average color.
                if (item._bhTint === undefined) {
                    if (item.img_blurhash) {
                        const px = bhDecode(item.img_blurhash, 1, 1);
                        item._bhTint = (px[0] << 16) | (px[1] << 8) | px[2];
                    } else {
                        item._bhTint = 0x808080;
                    }
                }
                tex  = Texture.WHITE;
                tint = item._bhTint;
            } else if (item.img_blurhash) {
                if (!item._bhTex) {
                    const BH_W = 8, BH_H = 8;
                    const pixels = bhDecode(item.img_blurhash, BH_W, BH_H);
                    const canvas = document.createElement('canvas');
                    canvas.width = BH_W; canvas.height = BH_H;
                    const ctx = canvas.getContext('2d');
                    const imageData = ctx.createImageData(BH_W, BH_H);
                    imageData.data.set(pixels);
                    ctx.putImageData(imageData, 0, 0);
                    item._bhTex = Texture.from(canvas);
                }
                tex = item._bhTex;
            }
        }

        const sprite  = new Sprite(tex);
        sprite.tint   = tint;
        sprite.x      = item._x;
        sprite.y      = item._y;
        sprite.width  = WALL_W;
        sprite.height = item._h;

        this._world.addChild(sprite);
        item._el          = sprite;
        item._sprite      = sprite;
        item._loadedTier  = item._cachedTier ?? -1;
        item._pendingTier = -1;
        this._mounted.add(item);
    }

    _unmountItem(item) {
        if (item._sprite) item._sprite.destroy({ texture: false });
        if (item._bhTex) { item._bhTex.destroy(true); item._bhTex = null; }
        item._el          = null;
        item._sprite      = null;
        item._loadedTier  = -1;
        item._pendingTier = -1;
        this._mounted.delete(item);
    }

    // ── URLs ──────────────────────────────────────────────────────────────────

    thumbUrl(item, tierIdx) {
        if (!item.file || !item.collectionId || !item.id) return null;
        return `${window.JK.pbUrl}/api/files/${item.collectionId}/${item.id}/${item.file}?thumb=${TIERS[tierIdx]}`;
    }

    fullUrl(item) {
        if (!item.file || !item.collectionId || !item.id) return '';
        return `${window.JK.pbUrl}/api/files/${item.collectionId}/${item.id}/${item.file}`;
    }

    bestThumbUrl(item) {
        for (let t = TIERS.length - 1; t >= 0; t--) {
            if (item._loadedTier >= t) return this.thumbUrl(item, t);
        }
        return this.thumbUrl(item, 1);
    }

    // ── RAF loop ──────────────────────────────────────────────────────────────

    _tick(now = performance.now()) {
        if (!this._pixiApp) return;
        const dt = Math.min(now - (this._lastTick ?? now), 64);
        this._lastTick = now;
        applyInertia(this, dt);
        const needsMore = this.updateScene();
        this._pixiApp.renderer.render(this._pixiApp.stage);
        // Post-render: textures queued for destruction are now safely out of
        // Pixi's batch state. See deferDestroy in gallery-virt.
        flushDeferredDestroys(this);
        const panKey = `${this.panX.toFixed(1)},${this.panY.toFixed(1)},${this.zoom.toFixed(3)}`;
        if (panKey !== this._lastPanKey || (this._crumbTrackingStarted && !this._crumbVisible)) {
            this._lastPanKey = panKey;
            const center = this._centerItem();
            if (!this._crumbInitialised) {
                this._crumbItem = center;
                this._crumbInitialised = true;
            }
            if (this._crumbTrackingStarted && (!this._crumbVisible || center !== this._crumbItem)) {
                this._crumbItem = center;
                this.updateCrumb(center);
                this._crumbVisible = true;
            }
        }
        const idle = !this._dragging
            && Math.abs(this.velX)    < 0.01
            && Math.abs(this.velY)    < 0.01
            && Math.abs(this.zoomVel) < 0.0005
            && this._tierLoading === 0
            && !needsMore;
        this._raf = idle ? null : requestAnimationFrame(t => this._tick(t));
    }

    _wake() {
        if (this._raf === null) {
            this._raf = requestAnimationFrame(t => this._tick(t));
        }
    }

    _maybeIdle() {
        if (Math.abs(this.velX) < 0.001 && Math.abs(this.velY) < 0.001 &&
            Math.abs(this.zoomVel) < 0.0005 && !this._dragging) {
            this._raf = null;
        }
    }

    // ── Events ────────────────────────────────────────────────────────────────

    bindEvents() {
        bindPointerEvents(this, this.viewport);
        document.getElementById('zoom-in') ?.addEventListener('click', () => this.zoomIn());
        document.getElementById('zoom-out')?.addEventListener('click', () => this.zoomOut());
    }

    // ── Zoom / pan ────────────────────────────────────────────────────────────

    zoomAt(cx, cy, factor) {
        const newZoom = Math.max(this.ZOOM_MIN, Math.min(ZOOM_MAX, this.zoom * factor));
        const ratio   = newZoom / this.zoom;
        this.panX = cx - ratio * (cx - this.panX);
        this.panY = cy - ratio * (cy - this.panY);
        this.zoom = newZoom;
        this._syncWorld();
    }

    _syncWorld() {
        if (!this._world || !this._pixiApp) return;
        this.clamp();
        this._world.x = this.panX;
        this._world.y = this.panY;
        this._world.scale.set(this.zoom);
        this._pixiApp.renderer.render(this._pixiApp.stage);
    }

    clamp() {
        const min = this.W < MOBILE_W ? CLAMP_MIN_PX_MOBILE : CLAMP_MIN_PX_DESKTOP;
        const ww  = this.worldW * this.zoom;
        const wh  = this.worldH * this.zoom;
        this.panX = Math.max(min - ww, Math.min(this.W - min, this.panX));
        this.panY = Math.max(min - wh, Math.min(this.H - min, this.panY));
    }

    zoomIn()  { stepZoom(this,  1); }
    zoomOut() { stepZoom(this, -1); }

    animateZoom(targetZ, screenCX, screenCY) {
        this.zoomVel = 0;
        this._wake();
        const startZ    = this.zoom;
        const startTime = performance.now();
        const tick = now => {
            const t     = Math.min(1, (now - startTime) / 300);
            const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            this.zoomAt(screenCX, screenCY, (startZ + (targetZ - startZ) * eased) / this.zoom);
            if (t < 1) requestAnimationFrame(tick);
            else this._maybeIdle();
        };
        requestAnimationFrame(tick);
    }

    // ── Click / lightbox ──────────────────────────────────────────────────────

    handleClick(sx, sy) {
        const item = this.itemAt(sx, sy);
        if (!item) return;
        const lbThreshold = this.W < 768 ? ZOOM_LIGHTBOX * 0.5 : ZOOM_LIGHTBOX;
        if (this.zoom >= lbThreshold) {
            this.openLightbox(item.id);
        } else {
            const screenCX = (item._x + WALL_W / 2) * this.zoom + this.panX;
            const screenCY = (item._y + item._h / 2) * this.zoom + this.panY;
            this.animateZoom(this.zoom * 2.5, screenCX, screenCY);
        }
    }

    _centerItem() {
        const cx = (this.W / 2 - this.panX) / this.zoom;
        const cy = (this.H / 2 - this.panY) / this.zoom;
        let best = null, bestDist = Infinity;

        const si = this._sortedItems;
        let lo = 0, hi = si.length - 1, start = 0;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (si[mid]._y + si[mid]._h >= cy) { start = mid; hi = mid - 1; }
            else lo = mid + 1;
        }

        const viewport = this.H / this.zoom;
        for (let i = Math.max(0, start - 1); i < si.length; i++) {
            const item = si[i];
            if (item._y > cy + viewport) break;
            if (!this._itemVisible(item)) continue;
            const dx = (item._x + WALL_W / 2) - cx;
            const dy = (item._y + item._h / 2) - cy;
            const d  = dx * dx + dy * dy;
            if (d < bestDist) { bestDist = d; best = item; }
        }
        return best;
    }

    updateCrumb(item) {
        const isAccessories = this.viewport.dataset.mode === 'accessories';
        const el = document.getElementById(isAccessories ? 'accessories-crumb' : 'wall-crumb');
        if (!el) return;

        const filter = this._filterCrumb
            ? `<span class="hidden md:inline">&nbsp;/ </span><span class="block md:inline">${this._filterCrumb}</span>`
            : '';

        const title = item && item.title
            ? `<span class="hidden md:inline">&nbsp;/ </span><span class="block md:inline">${item.title}</span>`
            : '';

        el.innerHTML = filter + title;
    }

    itemAt(sx, sy) {
        const wx = (sx - this.panX) / this.zoom;
        const wy = (sy - this.panY) / this.zoom;
        for (const item of this.items) {
            if (wx >= item._x && wx <= item._x + WALL_W &&
                wy >= item._y && wy <= item._y + item._h) return item;
        }
        return null;
    }

    async openLightbox(id) {
        const pool = this.items.filter(i => this._itemVisible(i));
        const index = pool.findIndex(i => i.id === id);
        if (index === -1) return;

        await this.ensureDims(pool[index]);

        const vpRect = this.viewport.getBoundingClientRect();

        const ARROW_PREV = '<div class="pswp-arrow"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="square" stroke-linejoin="square" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"/></svg></div>';
        const ARROW_NEXT = '<div class="pswp-arrow"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="square" stroke-linejoin="square" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" /></svg></div>';

        const pswp = new PhotoSwipe({
            trapFocus:    false,
            returnFocus:  false,
            arrowPrevSVG: ARROW_PREV,
            arrowNextSVG: ARROW_NEXT,
            closeSVG: '<div class="pswp-close"><svg viewBox="0 0 24 24"><path d="M 4.7070312 3.2929688 L 3.2929688 4.7070312 L 10.585938 12 L 3.2929688 19.292969 L 4.7070312 20.707031 L 12 13.414062 L 19.292969 20.707031 L 20.707031 19.292969 L 13.414062 12 L 20.707031 4.7070312 L 19.292969 3.2929688 L 12 10.585938 L 4.7070312 3.2929688 z"></path></svg></div>',
            zoomSVG: '<div class="pswp-zoom"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="square" stroke-linejoin="square" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg></div>',
            dataSource: pool.map(item => ({
                src:    this.fullUrl(item),
                msrc:   this.bestThumbUrl(item),
                width:  item._dims?.w || item.img_w || WALL_W * 4,
                height: item._dims?.h || item.img_h || item._h * 4,
            })),
            index,
            bgOpacity:          0.95,
            secondaryZoomLevel: 1,
            maxZoomLevel:       4,
            getThumbBoundsFn: i => {
                const it = pool[i];
                if (!it) return null;
                return {
                    x: it._x * this.zoom + this.panX + vpRect.left,
                    y: it._y * this.zoom + this.panY + vpRect.top,
                    w: WALL_W * this.zoom,
                };
            },
        });

        const onSlide = () => {
            const c = pswp.currIndex;
            [c - 1, c, c + 1].forEach(i => {
                if (i >= 0 && i < pool.length) this.ensureDims(pool[i]);
            });
            const item = pool[c];
            if (item) {
                this.updateCrumb(item);
                const shareUrl = `${location.origin}${location.pathname}?item=${item.id}`;
                showShare(item, shareUrl);
            }
        };
        pswp.on('change', onSlide);
        pswp.on('openingAnimationEnd', onSlide);
        pswp.on('beforeOpen', () => document.body.classList.add('lightbox-open'));
        pswp.on('close', () => {
            document.body.classList.remove('lightbox-open');
            history.replaceState(null, '', window.location.pathname + window.location.search);
            this.updateCrumb(this._crumbItem);
            hideShare();
        });

        pswp.on('loadComplete', e => {
            const img = e.slide.content.element;
            if (img && img.naturalWidth) {
                e.slide.data.width  = img.naturalWidth;
                e.slide.data.height = img.naturalHeight;
                e.slide.updateContentSize(true);
            }
        });

        pswp.init();
    }

    // ── URL param ─────────────────────────────────────────────────────────────

    titleToSlug(title) {
        return (title || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    }

    checkUrlParam() {
        const search = new URLSearchParams(window.location.search);
        // Desktop honours ?filter= as a visibility filter so the menu's wall
        // category links work there too. Mobile is already scoped at build time.
        if (!this._mobile) {
            const f = search.get('filter');
            if (f && f !== 'all' && this._categoryList.includes(f)) this.setFilter(f);
        }
        const param = search.get('item');
        if (!param) return;
        const item = this.items.find(i => i.id === param || this.titleToSlug(i.title) === param);
        if (!item) return;

        const targetZ = Math.max(this.zoom, this.ZOOM_LEVELS[1] ?? ZOOM_LIGHTBOX * 0.5);
        this.zoom = targetZ;
        this.panX = this.W / 2 - (item._x + WALL_W / 2) * targetZ;
        this.panY = this.H / 2 - (item._y + item._h  / 2) * targetZ;
        this.zoomVel = 0;
        this.velX = this.velY = 0;
        this._syncWorld();
        this._wake();
        requestAnimationFrame(() => this.openLightbox(item.id));
    }

    ensureDims(item) {
        if (item._dims) return Promise.resolve();
        return new Promise(resolve => {
            const img = new Image();
            img.onload  = () => { item._dims = { w: img.naturalWidth,  h: img.naturalHeight }; resolve(); };
            img.onerror = () => { item._dims = { w: 0, h: 0 }; resolve(); };
            img.src = this.fullUrl(item);
        });
    }
}

export function init() {
    // Always destroy the previous instance so its RAF + loads stop even when
    // navigating to a page without the wall (swup keeps JS alive).
    window.JK.wallGallery?.destroy();
    window.JK.wallGallery = null;
    if (document.getElementById('wall-viewport')) {
        window.JK.wallGallery = new WallGallery();
    }
}
